import SEO from "@/components/SEO";
import { BUSINESS } from "@/lib/constants";

export default function Terms() {
  return (
    <div data-testid="page-terms">
      <SEO
        title="Terms of Service — Carfornia Buyers"
        description="The terms that govern your use of carforniabuyers.com and how offer requests, inspections, written offers, paperwork and payment work with Carfornia Buyers."
        canonical="https://carforniabuyers.com/terms"
      />
      <section className="py-16 lg:py-24 bg-brand-surface">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-blue">Legal</p>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold text-slate-900 tracking-tight mt-3">Terms of Service</h1>
          <p className="text-slate-500 mt-3 text-sm">Last updated: October 2026</p>
        </div>
      </section>

      <section className="py-12 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 space-y-7 text-slate-700 leading-relaxed">
          <p>These Terms of Service (“Terms”) govern your use of carforniabuyers.com (the “Site”) and the vehicle-purchase services offered by {BUSINESS.name}, located at {BUSINESS.address}. By using the Site you agree to these Terms.</p>

          <Section title="Offer requests are not purchase offers">
            <p>Submitting the form on the Site is a request for an offer. It is not a purchase offer, a price quote or a commitment by either party. We are not obligated to make an offer on any vehicle, and you are not obligated to sell. Any figure discussed by phone or text before an inspection is a preliminary estimate only.</p>
          </Section>

          <Section title="Inspection and written offers">
            <p>A purchase offer is made only after we have inspected the vehicle in person and verified the VIN, title and mileage. The offer is given to you in writing and states the price, any conditions and how long it remains open. If the vehicle does not match the description you provided, we may decline to make an offer or make a different offer. You may accept or decline any offer.</p>
          </Section>

          <Section title="Eligibility">
            <p>You must be at least 18 years old and the registered owner of the vehicle, or have written authority from the owner to sell it. You represent that the information you provide about yourself and the vehicle is accurate and that you will disclose any loan, lien, salvage or rebuilt title, or lost title before the inspection.</p>
          </Section>

          <Section title="Paperwork">
            <p>If you accept an offer, you agree to sign the California Certificate of Title and odometer disclosure, provide valid government photo identification, and deliver all keys. We will help you complete the DMV Notice of Transfer and Release of Liability. If there is a loan on the vehicle, the payoff will be handled with your lender as agreed in writing before the sale.</p>
          </Section>

          <Section title="Payment">
            <p>Payment is made the same day the sale is completed, using the payment method agreed in the written offer. We do not take possession of the vehicle until payment has been arranged.</p>
          </Section>

          <Section title="Satisfaction guarantee">
            <p>Your satisfaction is our priority. If you are not satisfied with how we handled your sale, contact us at {BUSINESS.phone} and we will work with you in good faith to make it right. This guarantee does not entitle you to unwind a completed sale or to a price above the written offer you accepted.</p>
          </Section>

          <Section title="Use of the Site">
            <p>You agree not to submit false information, to use the Site for any unlawful purpose, or to attempt to access data or systems that are not intended for you.</p>
          </Section>

          <Section title="Disclaimer and limitation of liability">
            <p>The Site is provided “as is.” To the maximum extent permitted by California law, {BUSINESS.name} is not liable for indirect, incidental or consequential damages arising from your use of the Site or our services, and our total liability in connection with any vehicle purchase shall not exceed the purchase price stated in the written offer you accepted.</p>
          </Section>

          <Section title="Governing law">
            <p>These Terms are governed by the laws of the State of California. Any dispute will be resolved in the state or federal courts located in Alameda County, California.</p>
          </Section>

          <Section title="Changes and contact">
            <p>We may update these Terms from time to time; changes take effect when posted here with a new “last updated” date. Questions? Call or text {BUSINESS.phone}, use the contact form, or write to {BUSINESS.name}, {BUSINESS.address}.</p>
          </Section>
        </div>
      </section>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <h2 className="font-display text-xl font-bold text-slate-900 mt-8 mb-3">{title}</h2>
      {children}
    </div>
  );
}
